import { buttonPalette } from "./colorPalette";

const TIERS = ["primary", "secondary", "tertiary", "success", "danger", "orion"];
const STYLES = ["filled", "outlined", "ghost"];

const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
const variantName = (style, tier) => `${style}${cap(tier)}`;

const BASE_BUTTON = {
  fontFamily: "Figtree",
  fontWeight: 500,
  lineHeight: 1,
  borderRadius: "999px",
  border: "1.5px solid transparent",
  textTransform: "none",
  whiteSpace: "nowrap",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  "& .MuiButton-startIcon, & .MuiButton-endIcon": {
    marginLeft: 0,
    marginRight: 0,
  },
  transition:
    "background 140ms cubic-bezier(0.22, 0.61, 0.36, 1)," +
    " color 140ms cubic-bezier(0.22, 0.61, 0.36, 1)," +
    " border-color 140ms cubic-bezier(0.22, 0.61, 0.36, 1)," +
    " box-shadow 140ms cubic-bezier(0.22, 0.61, 0.36, 1)," +
    " transform 140ms cubic-bezier(0.22, 0.61, 0.36, 1)",
  "&:focus-visible": {
    outline: "none",
  },
};

// Opt-in via size="small" — smaller than the default.
const SIZE_SMALL = {
  fontSize: "14px",
  padding: "0 20px",
  height: "38px",
};

// Default ("medium") sizing — matches the portal button.
const SIZE_DEFAULT = {
  fontSize: "16px",
  padding: "0 22px",
  height: "44px",
};

// Opt-in via size="large".
const SIZE_LARGE = {
  fontSize: "18px",
  padding: "0 24px",
  height: "50px",
};

const focusRing = (c) => ({
  "&:focus-visible": {
    outline: "none",
    boxShadow: `0 0 0 3px color-mix(in srgb, ${c} 35%, transparent)`,
  },
});

const styleFor = (style, { c, fg }) => {
  switch (style) {
    case "filled":
      return {
        backgroundColor: c,
        color: fg,
        borderColor: "transparent",
        "&:hover": {
          backgroundColor: `color-mix(in srgb, ${c} 84%, white)`,
        },
        "&:active": {
          backgroundColor: `color-mix(in srgb, ${c} 84%, black)`,
          transform: "translateY(0.5px)",
        },
        "&.Mui-disabled, &:disabled": {
          backgroundColor: `color-mix(in srgb, ${c} 26%, white)`,
          color: fg,
          borderColor: "transparent",
        },
        ...focusRing(c),
      };
    case "outlined":
      return {
        backgroundColor: "transparent",
        color: c,
        borderColor: c,
        "&:hover": {
          backgroundColor: `color-mix(in srgb, ${c} 10%, transparent)`,
        },
        "&:active": {
          backgroundColor: `color-mix(in srgb, ${c} 18%, transparent)`,
          transform: "translateY(0.5px)",
        },
        "&.Mui-disabled, &:disabled": {
          color: `color-mix(in srgb, ${c} 38%, white)`,
          borderColor: `color-mix(in srgb, ${c} 24%, white)`,
          backgroundColor: "transparent",
        },
        ...focusRing(c),
      };
    case "ghost":
      return {
        backgroundColor: "transparent",
        color: c,
        borderColor: "transparent",
        "&:hover": {
          backgroundColor: `color-mix(in srgb, ${c} 10%, transparent)`,
        },
        "&:active": {
          backgroundColor: `color-mix(in srgb, ${c} 18%, transparent)`,
          transform: "translateY(0.5px)",
        },
        "&.Mui-disabled, &:disabled": {
          color: `color-mix(in srgb, ${c} 38%, white)`,
          backgroundColor: "transparent",
          borderColor: "transparent",
        },
        ...focusRing(c),
      };
    default:
      return {};
  }
};

export const createButtonVariants = (palette = buttonPalette) => {
  const merged = {
    primary:   palette.primary   ?? buttonPalette.primary,
    secondary: palette.secondary ?? buttonPalette.secondary,
    tertiary:  palette.tertiary  ?? buttonPalette.tertiary,
    success:   palette.success   ?? buttonPalette.success,
    danger:    palette.danger    ?? buttonPalette.danger,
    orion:     palette.orion     ?? buttonPalette.orion,
  };

  const out = [];
  STYLES.forEach((style) => {
    TIERS.forEach((tier) => {
      const name = variantName(style, tier);
      const tokens = merged[tier];

      // Base entry: default (medium) sizing + the style/tier color rules.
      out.push({
        props: { variant: name },
        style: {
          ...BASE_BUTTON,
          ...SIZE_DEFAULT,
          ...styleFor(style, tokens),
        },
      });

      // Small opt-in: only sizing overrides, colors inherit from base.
      out.push({
        props: { variant: name, size: "small" },
        style: SIZE_SMALL,
      });

      // Large opt-in: only sizing overrides, colors inherit from base.
      out.push({
        props: { variant: name, size: "large" },
        style: SIZE_LARGE,
      });
    });
  });
  return out;
};

export const buttonVariants = createButtonVariants();
